import useForm from 'antd/es/form/hooks/useForm';
import { IconDeviceFloppy } from '@tabler/icons-react';
import { useTheme } from 'styled-components';
import { AxiosError } from 'axios';
import { AppForm, AppFormProps } from 'components/organisms';
import { commonTexts, enrollmentTexts } from 'consts/text';
import { AppCheckbox, AppCheckboxGroup, AppFormItem, DetailsFrameBase } from 'components/atoms';
import {
    BirthDateDatePickerElement,
    BirthPlaceInputElement,
    CityInputElement,
    EmailInputElement,
    FormNavButtons,
    GenderPickerElement,
    PeselInputElement,
    PhoneNumberInputElement,
    SimpleInputElement,
    StreetAddressInputElement,
    ZipCodeInputElement,
} from 'components/molecules';
import { formFields } from 'consts/form/formFields';
import { max300LengthRule } from 'consts/rules/textRules';
import { ChildEnrollmentFormFields, CommonFormProps } from 'types';
import { enrollmentAdditionalCriteriaFields } from 'consts/enrollment/enrollmentAdditionalCriteriaFields';

type ChildEnrollmentAddEditFormProps = Omit<
    AppFormProps<ChildEnrollmentFormFields>,
    'width' | 'name' | 'onFinish' | 'error' | 'withGoBack'
> &
    Pick<CommonFormProps, 'mode'> & {
        axiosError?: AxiosError | null;
        isLoading?: boolean;
        onFinish: (childEnrollmentFormFields: ChildEnrollmentFormFields) => void;
    };

// TODO: to disable all inputs for archived enrollment + to disable button
export const ChildEnrollmentAddEditForm = ({
    mode,
    form,
    initialValues,
    axiosError,
    ...props
}: ChildEnrollmentAddEditFormProps) => {
    const [formInstance] = useForm(form);

    const theme = useTheme();

    const isAddMode = mode === 'add';
    const formName = isAddMode ? 'childEnrollmentAdd' : 'childEnrollmentEdit';
    const title = isAddMode ? enrollmentTexts.addForm.title : enrollmentTexts.editForm.title;

    const childEnrollmentAddEditError = axiosError ? commonTexts.errorMessages.unknown : '';

    return (
        <AppForm<ChildEnrollmentFormFields>
            name={formName}
            form={formInstance}
            title={title}
            error={childEnrollmentAddEditError}
            maxWidth={700}
            withGoBack={!isAddMode}
            {...props}
        >
            <DetailsFrameBase
                title={enrollmentTexts.addForm.parentOrGuardianData}
                headerColor={theme.colors.primary.primary9}
                noFrame
                smallHeaderMargin
                noMarginLeft
            >
                <AppFormItem justifyContent="space-between" doubleItem>
                    <SimpleInputElement
                        label={commonTexts.dataLabels.firstName}
                        name={[formFields.enrollmentParentData, formFields.firstName]}
                        rules={[max300LengthRule]}
                    />
                    <SimpleInputElement
                        label={commonTexts.dataLabels.lastName}
                        name={[formFields.enrollmentParentData, formFields.lastName]}
                        rules={[max300LengthRule]}
                    />
                </AppFormItem>
                <AppFormItem justifyContent="space-between" doubleItem>
                    <EmailInputElement name={[formFields.enrollmentParentData, formFields.email]} />
                    <PhoneNumberInputElement
                        name={[formFields.enrollmentParentData, formFields.phoneNumber]}
                    />
                </AppFormItem>
            </DetailsFrameBase>
            <DetailsFrameBase
                title={enrollmentTexts.addForm.childPersonalData}
                headerColor={theme.colors.primary.primary9}
                noFrame
                smallHeaderMargin
                noMarginLeft
            >
                <AppFormItem justifyContent="space-between" doubleItem>
                    <SimpleInputElement
                        label={commonTexts.dataLabels.firstName}
                        name={[formFields.enrollmentChildData, formFields.firstName]}
                        rules={[max300LengthRule]}
                    />
                    <SimpleInputElement
                        label={commonTexts.dataLabels.lastName}
                        name={[formFields.enrollmentChildData, formFields.lastName]}
                        rules={[max300LengthRule]}
                    />
                </AppFormItem>
                <AppFormItem justifyContent="space-between" doubleItem>
                    <PeselInputElement
                        name={[formFields.enrollmentChildData, formFields.peselNumber]}
                        disabled={!isAddMode}
                    />
                    <GenderPickerElement name={[formFields.enrollmentChildData, formFields.sex]} />
                </AppFormItem>
                <AppFormItem justifyContent="space-between" doubleItem>
                    <BirthDateDatePickerElement
                        name={[formFields.enrollmentChildData, formFields.birthDate]}
                    />
                    <BirthPlaceInputElement
                        name={[formFields.enrollmentChildData, formFields.birthPlace]}
                    />
                </AppFormItem>
            </DetailsFrameBase>
            <DetailsFrameBase
                title={enrollmentTexts.addForm.childAddress}
                headerColor={theme.colors.primary.primary9}
                noFrame
                smallHeaderMargin
                noMarginLeft
            >
                <StreetAddressInputElement
                    name={[formFields.enrollmentChildAddressData, formFields.street]}
                    optional
                />
                <AppFormItem justifyContent="space-between" doubleItem>
                    <ZipCodeInputElement
                        name={[formFields.enrollmentChildAddressData, formFields.zipCode]}
                        optional
                    />
                    <CityInputElement
                        name={[formFields.enrollmentChildAddressData, formFields.city]}
                        optional
                    />
                </AppFormItem>
            </DetailsFrameBase>
            <DetailsFrameBase
                title={enrollmentTexts.addForm.additional}
                headerColor={theme.colors.primary.primary9}
                noFrame
                smallHeaderMargin
                noMarginLeft
            >
                <AppFormItem
                    name={formFields.additionalConditions}
                    marginBottom={12}
                    label={enrollmentTexts.addForm.additionalCondition.label}
                    optional
                    hasOptionalText
                >
                    <AppCheckboxGroup>
                        <AppCheckbox
                            value={enrollmentAdditionalCriteriaFields.multipleChildrenFamily}
                        >
                            {enrollmentTexts.addForm.additionalCondition.largeFamily}
                        </AppCheckbox>
                        <AppCheckbox value={enrollmentAdditionalCriteriaFields.childDisability}>
                            {enrollmentTexts.addForm.additionalCondition.childDisability}
                        </AppCheckbox>
                        <AppCheckbox value={enrollmentAdditionalCriteriaFields.siblingDisability}>
                            {enrollmentTexts.addForm.additionalCondition.siblingDisability}
                        </AppCheckbox>
                        <AppCheckbox
                            value={enrollmentAdditionalCriteriaFields.bothParentsDisability}
                        >
                            {enrollmentTexts.addForm.additionalCondition.bothParentDisability}
                        </AppCheckbox>
                        <AppCheckbox value={enrollmentAdditionalCriteriaFields.oneParentDisability}>
                            {enrollmentTexts.addForm.additionalCondition.parentDisability}
                        </AppCheckbox>
                        <AppCheckbox value={enrollmentAdditionalCriteriaFields.singleParenting}>
                            {enrollmentTexts.addForm.additionalCondition.singleParenting}
                        </AppCheckbox>
                        <AppCheckbox value={enrollmentAdditionalCriteriaFields.fosterCare}>
                            {enrollmentTexts.addForm.additionalCondition.fosterCare}
                        </AppCheckbox>
                    </AppCheckboxGroup>
                </AppFormItem>
            </DetailsFrameBase>
            <FormNavButtons
                formValuesBeforeChanges={initialValues}
                buttonText={commonTexts.actionLabels.save}
                leftIcon={<IconDeviceFloppy size={20} />}
            />
        </AppForm>
    );
};
